// BetterPOS design tokens. Apple system colors in light mode + DRGA
// brand accent. These are the single source of truth; Tailwind classes
// in the consuming apps are expected to resolve to these values via the
// pos-web `@theme` block in `apps/pos-web/src/index.css`.
//
// Touch targets follow CLAUDE.md Code style: ≥ 44 pt everywhere a
// person taps.
//
// The range-orange hex is a working value until the owner confirms
// (`OPEN-QUESTIONS.md` H6).

export const colors = {
  // DRGA brand
  drgaNavy: "#052460",
  rangeOrange: "#e8541e",

  // iOS semantic colors (light mode)
  iosBlue: "#007aff",
  iosGreen: "#34c759",
  iosOrange: "#ff9500",
  iosRed: "#ff3b30",
  iosYellow: "#ffcc00",

  // Neutrals (Tailwind slate scale — matches the mocks' palette)
  white: "#ffffff",
  slate: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },

  // iOS grouped-list background (the whole-app canvas)
  groupedBackground: "#f2f2f7",
  // Secondary (card) surface inside a grouped list
  secondaryGroupedBackground: "#ffffff",
  // Hairline separator (0.5pt on 2x displays)
  separator: "#e5e7eb",
} as const;

// 4pt grid. Not every value needs to resolve to a Tailwind utility — the
// values here are the design intent, implementers pick the nearest class.
export const spacing = {
  touchTargetPt: 44,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
} as const;

// Type scale matching the mocks: iOS large-title at 34pt down through a
// 12pt caption. The weights pair with system-ui so SF Pro / Segoe UI
// Variable render naturally.
export const typography = {
  largeTitle: { sizePt: 34, weight: 700, lineHeight: 1.1 },
  title1: { sizePt: 28, weight: 700, lineHeight: 1.15 },
  title2: { sizePt: 22, weight: 600, lineHeight: 1.2 },
  title3: { sizePt: 20, weight: 600, lineHeight: 1.25 },
  headline: { sizePt: 17, weight: 600, lineHeight: 1.3 },
  body: { sizePt: 17, weight: 400, lineHeight: 1.4 },
  callout: { sizePt: 15, weight: 400, lineHeight: 1.4 },
  footnote: { sizePt: 13, weight: 400, lineHeight: 1.4 },
  caption1: { sizePt: 12, weight: 400, lineHeight: 1.35 },
  caption2: { sizePt: 11, weight: 500, lineHeight: 1.3 },
} as const;

// iOS-ish corner radii.
export const radii = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  full: 9999,
} as const;

// Semantic tones used by StatusBadge, Row leading icons, Toast variants.
export const tones = {
  info: { fg: colors.iosBlue, bgTint: "#dbeafe" },
  ok: { fg: colors.iosGreen, bgTint: "#d1fae5" },
  warn: { fg: colors.iosOrange, bgTint: "#fef3c7" },
  danger: { fg: colors.iosRed, bgTint: "#fee2e2" },
} as const;

export type Tone = keyof typeof tones;

export const tokens = { colors, spacing, typography, radii, tones } as const;
